import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { C } from "./homeTheme";
import {
  formatEtherWithDecimals,
  formatThousands,
  isValidAddress,
} from "@/lib/utils";
import { useFortAgentTransferSign } from "@/hooks/api/useFortAgentTransferSign";
import { useFortAgentPayMixedFusd } from "@/hooks/api/useFortAgentPayMixedFusd";
import { showError, showSuccess } from "@/lib/toast";
import { useQueryClient } from "@tanstack/react-query";
import { useConnection, useWriteContract } from "wagmi";
import { simulateContract, switchChain } from "@wagmi/core/actions";
import { wagmiConfig } from "@/config";
import {
  CCC_FUSD_ADDRESS,
  FORTTRADE_CONTRACT_ADDRESS,
  USDT_ADDRESS,
} from "@/config/env";
import { FortTradeAbi } from "@/abi/FortTrade.abi";
import { parseChainError } from "@/lib/chainError";
import { useTranslation } from "react-i18next";
import {
  FortAgentPayMixedFusdResultCode,
  FortAgentTransferResultCode,
  MyFortAgentItem,
} from "@/api/types";
import { getTokenName } from "@/config/common";
import { bscChainId } from "@/config/wagmi";

export function TransferDialog({
  open,
  onClose,
  agent,
}: {
  open: boolean;
  onClose: () => void;
  agent: MyFortAgentItem;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { address: walletAddress } = useConnection();
  const { mutateAsync: transferSignAsync } = useFortAgentTransferSign();
  const { mutateAsync: payMixedFusdAsync, isPending: payingFusd } =
    useFortAgentPayMixedFusd();
  const writeContract = useWriteContract();
  const [transferring, setTransferring] = useState(false);
  const [address, setAddress] = useState("");
  /** 本弹窗内已完成延期 FUSD 扣款（进入转让下一步） */
  const [fusdPaidInDialog, setFusdPaidInDialog] = useState(false);
  /** 转让前是否必须先完成混合购延期 FUSD 中心化扣款 */
  const mustPayFusdFirst =
    agent.paymentMode === "mixed" &&
    !agent.fusdPurchaseDeducted &&
    BigInt(agent.purchasePayFusdWei || "0") > 0n;

  useEffect(() => {
    if (open) setFusdPaidInDialog(false);
  }, [open, agent.agentId]);

  const showTransferStep = !mustPayFusdFirst || fusdPaidInDialog;
  const showFusdPaidBadge =
    showTransferStep &&
    BigInt(agent.purchasePayFusdWei || "0") > 0n &&
    (agent.fusdPurchaseDeducted || fusdPaidInDialog);

  const handleClose = () => {
    onClose();
  };

  const failAndClose = () => {
    queryClient.invalidateQueries({ queryKey: ["myFortAgents"] });
    onClose();
  };

  const handlePayFusd = async () => {
    if (!walletAddress) {
      showError(t("toast.common.connectWalletFirst"));
      return;
    }
    try {
      const data = await payMixedFusdAsync({ agentId: agent.agentId });
      if (data.resultCode === FortAgentPayMixedFusdResultCode.Success) {
        setFusdPaidInDialog(true);
      } else {
        failAndClose();
      }
    } catch {
      failAndClose();
    }
  };

  const handleTransfer = async () => {
    if (mustPayFusdFirst && !fusdPaidInDialog && !agent.fusdPurchaseDeducted) {
      showError(
        t("homePage.transferDialog.completeFusdFirst", {
          tokenName: getTokenName(CCC_FUSD_ADDRESS),
        })
      );
      return;
    }
    if (!address || !isValidAddress(address)) {
      showError(t("homePage.transferDialog.invalidAddress"));
      return;
    }
    if (address === agent.walletAddress) {
      showError(t("homePage.transferDialog.cannotTransferToSelf"));
      return;
    }
    await switchChain(wagmiConfig, { chainId: bscChainId });
    setTransferring(true);
    try {
      const res = await transferSignAsync({
        agentId: agent.agentId,
        to: address,
      });
      if (res.resultCode !== FortAgentTransferResultCode.Success) {
        failAndClose();
        return;
      }

      const { request } = await simulateContract(wagmiConfig, {
        address: FORTTRADE_CONTRACT_ADDRESS as `0x${string}`,
        abi: FortTradeAbi,
        functionName: "transfer",
        args: [
          address as `0x${string}`,
          BigInt(agent.agentId),
          BigInt(res.deadline),
          BigInt(res.nonce),
          res.signature as `0x${string}`,
        ],
      });
      await writeContract.mutateAsync(request);
      showSuccess(t("homePage.transferDialog.transferSubmitted"));
      queryClient.invalidateQueries({ queryKey: ["myFortAgents"] });
      handleClose();
    } catch (error) {
      console.error("transferErr", error);
      const errorKey = parseChainError(error);
      showError(t(errorKey));
      failAndClose();
    } finally {
      setTransferring(false);
      setAddress("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-sm mx-auto"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("homePage.transferDialog.title")}
          </DialogTitle>
        </DialogHeader>

        {!showTransferStep ? (
          <div className="space-y-4 py-2">
            <div
              className="p-3 rounded-lg text-xs"
              style={{
                background: "rgba(245,158,11,0.06)",
                border: `1px solid rgba(245,158,11,0.1)`,
                color: C.amber,
              }}
            >
              <div className="flex items-center gap-2 font-semibold mb-1">
                <AlertCircle size={14} />
                {t("homePage.transferDialog.fusdPendingTitle", {
                  tokenName: getTokenName(CCC_FUSD_ADDRESS),
                })}
              </div>
              <p>
                {t("homePage.transferDialog.fusdPendingDesc", {
                  amount: formatThousands(
                    formatEtherWithDecimals(agent.purchasePayFusdWei)
                  ),
                  tokenName: getTokenName(CCC_FUSD_ADDRESS),
                  tokenNameUsdt: getTokenName(USDT_ADDRESS),
                })}
              </p>
            </div>
            <div
              className="rounded-lg p-3"
              style={{ background: C.card2, border: `1px solid ${C.border}` }}
            >
              <div className="flex justify-between text-sm">
                <span style={{ color: C.muted }}>
                  {t("homePage.transferDialog.fusdPayLabel", {
                    tokenName: getTokenName(CCC_FUSD_ADDRESS),
                  })}
                </span>
                <span className="mono-num font-bold" style={{ color: C.amber }}>
                  {formatThousands(
                    formatEtherWithDecimals(agent.purchasePayFusdWei)
                  )}{" "}
                  {getTokenName(CCC_FUSD_ADDRESS)}
                </span>
              </div>
            </div>
            {!agent.fusdPurchaseDeducted && +agent.purchasePayFusdWei > 0 && (
              <div
                className="rounded-lg p-3 space-y-2 text-xs"
                style={{
                  background: "rgba(245,158,11,0.06)",
                  border: `1px solid rgba(245,158,11,0.2)`,
                }}
              >
                <div
                  className="flex items-center gap-1.5 font-semibold mb-2"
                  style={{ color: C.amber }}
                >
                  <AlertCircle size={14} />
                  {t("homePage.transferDialog.fusdDetailTitle", {
                    tokenName: getTokenName(CCC_FUSD_ADDRESS),
                  })}
                </div>
                <div
                  className="space-y-1.5 text-[11px]"
                  style={{ color: C.text }}
                >
                  <div className="flex justify-between">
                    <span>{t("homePage.transferDialog.fusdTotal")}</span>
                    <span
                      className="mono-num font-semibold"
                      style={{ color: C.amber }}
                    >
                      {formatThousands(
                        formatEtherWithDecimals(agent.purchasePayFusdWei)
                      )}{" "}
                      {getTokenName(CCC_FUSD_ADDRESS)}
                    </span>
                  </div>
                  <div
                    className="flex justify-between"
                    style={{ color: C.muted }}
                  >
                    <span>
                      {t("homePage.transferDialog.fusdFromGame", {
                        tokenName: getTokenName(CCC_FUSD_ADDRESS),
                      })}
                    </span>
                    <span className="mono-num">
                      {formatThousands(
                        formatEtherWithDecimals(
                          BigInt(agent.purchasePayFusdFromGameBalanceWei)
                        )
                      )}
                      {getTokenName(CCC_FUSD_ADDRESS)}
                    </span>
                  </div>
                  <div
                    className="flex justify-between"
                    style={{ color: C.muted }}
                  >
                    <span>
                      {t("homePage.transferDialog.fusdFromWithdraw", {
                        tokenName: getTokenName(CCC_FUSD_ADDRESS),
                      })}
                    </span>
                    <span className="mono-num">
                      {formatThousands(
                        formatEtherWithDecimals(
                          BigInt(agent.purchasePayFusdFromWaitClaimWei)
                        )
                      )}
                      {getTokenName(CCC_FUSD_ADDRESS)}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <button
              type="button"
              className="w-full py-2.5 rounded-lg font-bold text-sm btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={payingFusd}
              onClick={handlePayFusd}
            >
              {payingFusd
                ? t("homePage.transferDialog.payingFusd")
                : t("homePage.transferDialog.payFusd", {
                    amount: formatThousands(
                      formatEtherWithDecimals(agent.purchasePayFusdWei)
                    ),
                    tokenName: getTokenName(CCC_FUSD_ADDRESS),
                  })}
            </button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* {showFusdPaidBadge && (
              <div
                className="p-2 rounded-lg text-[10px] flex items-center gap-1.5"
                style={{ background: "rgba(34,197,94,0.06)", color: C.green }}
              >
                <CheckCircle2 size={12} />
                {t("homePage.transferDialog.fusdPaid")}
              </div>
            )} */}
            <div
              className="p-3 rounded-lg text-xs"
              style={{
                background: "rgba(239,68,68,0.05)",
                border: "1px solid rgba(239,68,68,0.1)",
                color: C.red,
              }}
            >
              {t("homePage.transferDialog.warning")}
            </div>
            <div className="space-y-2">
              <label className="text-xs" style={{ color: C.muted }}>
                {t("homePage.transferDialog.recipientLabel")}
              </label>
              <input
                className="w-full rounded-lg p-3 text-sm"
                style={{
                  background: C.card2,
                  border: `1px solid ${C.border}`,
                  color: C.text,
                }}
                placeholder={t("homePage.transferDialog.recipientPlaceholder")}
                value={address}
                onChange={e => setAddress(e.target.value)}
              />
            </div>
            <Button
              className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20"
              disabled={transferring}
              onClick={handleTransfer}
            >
              {t("homePage.transferDialog.confirmTransfer")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
